import { useEffect, useState } from "react";
import { Heading } from "../components/heading";
import { Fieldset, Label } from "../components/fieldset";
import { Select } from "../components/select";
import { Input } from "../components/input";
import { Button } from "../components/button";
import { BSCustomer } from "@/models/customer";
import { BSHorse } from "@/models/horse";
import { BSService } from "@/models/service";
import { BSBillItem } from "@/models/billItem";
import { collection, doc, onSnapshot, query, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import * as Headless from "@headlessui/react";
import { Alert, AlertDescription, AlertTitle } from "../components/alert";
import { BSGroom } from "@/models/groom";
import { BSPayrollItem } from "@/models/payrollItem";
import { getNextPaymentDate } from "../helpers/date.helpers";

export default function Form() {
  // Data states
  const [riders, setRiders] = useState<{ id: string; name: string, customerName: string }[]>([]);

  const [horses, setHorses] = useState<BSHorse[]>([]);
  const [services, setServices] = useState<BSService[]>([]);
  const [grooms, setGrooms] = useState<BSGroom[]>([]);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedHorse, setSelectedHorse] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [selectedGroom, setSelectedGroom] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [comments, setComments] = useState("");

  // Alert state
  const [showAlert, setShowAlert] = useState(false);

  // Fetch data
  useEffect(() => {
    const fetchCustomers = () => {
      const q = query(collection(db, "customers"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSCustomer[] = [];
        const riders: { id: string; name: string, customerName: string }[] = [];
        querySnapshot.forEach((doc) => {
          const customerData = doc.data() as BSCustomer;
          items.push({ ...customerData, id: doc.id });
          const riderObjects = (customerData.riders ?? []).map(rider => ({ id: customerData.id, name: rider, customerName: customerData.name }));
          riders.push(...riderObjects);
        });
        setRiders(riders);
      });
    };

    const fetchHorses = () => {
      const q = query(collection(db, "horses"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSHorse[] = [];
        querySnapshot.forEach((doc) => {
          const horseData = doc.data() as BSHorse;
          items.push({ ...horseData, id: doc.id });
        });
        setHorses(items);
      });
    };

    const fetchServices = () => {
      const q = query(collection(db, "services"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSService[] = [];
        querySnapshot.forEach((doc) => {
          const serviceData = doc.data() as BSService;
          if (serviceData.public) {
            items.push({ ...serviceData, id: doc.id });
          }
        });
        setServices(items);
      });
    };

    const fetchGrooms = () => {
      const q = query(collection(db, "grooms"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSGroom[] = [];
        querySnapshot.forEach((doc) => {
          const groomData = doc.data() as BSGroom;
          items.push({ ...groomData, id: doc.id });
        });
        setGrooms(items);
      });
    };

    fetchCustomers();
    fetchHorses();
    fetchServices();
    fetchGrooms();
  }, []);

  const resetForm = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedCustomer("");
    setSelectedHorse("");
    setSelectedService("");
    setQuantity(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const serviceId = selectedService.split('-')[0];
      const needsGroom = selectedService.split('-')[1];
      const selectedServiceObj = services.find(s => s.id === serviceId);
      const selectedHorseObj = horses.find(h => h.id === selectedHorse);
      const selectedGroomObj = grooms.find(g => g.id === selectedGroom);

      if (!selectedServiceObj || !selectedHorseObj) {
        throw new Error("Missing required data");
      }

      // Calculate total value based on service value and quantity
      const totalValue = selectedServiceObj.price * quantity;

      const newBillItem: Omit<BSBillItem, 'id'> = {
        customerId: selectedCustomer.length > 0 ? selectedCustomer.split('-')[0] : selectedHorseObj.customerId,
        groomId: selectedGroom,
        horseId: selectedHorse,
        serviceId: serviceId,
        date: new Date(selectedDate),
        quantity: quantity,
        value: totalValue,
        location: selectedHorseObj.location,
        comments: comments,
        exported: false
      };

      const newDocRef = doc(collection(db, 'billItems'));
      await setDoc(newDocRef, { ...newBillItem, id: newDocRef.id });

      if (selectedGroomObj && needsGroom === 'true') {
        const newPayrollItem: Omit<BSPayrollItem, 'id'> = {
          groomId: selectedGroom,
          item: selectedServiceObj.productServiceName,
          value: totalValue,
          date: new Date(selectedDate),
          paymentDate: getNextPaymentDate(new Date(selectedDate)),
          paid: false
        };

        const newPayrollDocRef = doc(collection(db, 'payroll'));
        await setDoc(newPayrollDocRef, { ...newPayrollItem, id: newPayrollDocRef.id });
      }
      
      setShowAlert(true);
      resetForm();
      
      // Hide alert after 3 seconds
      setTimeout(() => setShowAlert(false), 3000);
    } catch (error) {
      console.error('Error creating bill item:', error);
      // TODO: Add error handling
    }
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Service Form</Heading>
      </div>

      <form onSubmit={handleSubmit} className="mt-6">
        <Fieldset>
          <div className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3">
            <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
              <Label>Date*</Label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="mt-3 sm:col-span-2 sm:mt-0"
                required
              />
            </Headless.Field>

            <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
              <Label>Horse*</Label>
              <Select
                value={selectedHorse}
                onChange={(e) => setSelectedHorse(e.target.value)}
                className="mt-3 sm:col-span-2 sm:mt-0"
                required
              >
                <option value="">Select a horse...</option>
                {horses
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(horse => (
                    <option key={horse.id} value={horse.id}>
                      {horse.name}
                    </option>
                  ))}
              </Select>
            </Headless.Field>

            <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
              <Label>Rider</Label>
              <Select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className="mt-3 sm:col-span-2 sm:mt-0"
              >
                <option value="">Select a rider...</option>
                {riders
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(rider => (
                    <option key={`${rider.id}-${rider.name}`} value={`${rider.id}-${rider.name}`}>
                      {rider.name} ({rider.customerName})
                    </option>
                  ))}
              </Select>
            </Headless.Field>

            <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
              <Label>Service*</Label>
              <Select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="mt-3 sm:col-span-2 sm:mt-0"
                required
              >
                <option value="">Select a service...</option>
                {services
                  .sort((a, b) => a.productServiceName.localeCompare(b.productServiceName))
                  .map(service => (
                    <option key={`${service.id}-${service.needsGroom}`} value={`${service.id}-${service.needsGroom}`}>
                      {service.productServiceName}
                    </option>
                  ))}
              </Select>
            </Headless.Field>

            <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
              <Label>Quantity*</Label>
              <Input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                className="mt-3 sm:col-span-2 sm:mt-0"
                required
              />
            </Headless.Field>

            {selectedService.includes('-true') && (
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Groom*</Label>
                <Select
                value={selectedGroom}
                onChange={(e) => setSelectedGroom(e.target.value)}
                className="mt-3 sm:col-span-2 sm:mt-0"
                required
              >
                <option value="">Select a groom...</option>
                {grooms
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(groom => (
                    <option key={groom.id} value={groom.id}>
                      {groom.name}
                    </option>
                  ))}
              </Select>
              </Headless.Field>
            )}

            <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
              <Label>Comments</Label>
              <Input
                type="text"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="mt-3 sm:col-span-2 sm:mt-0"
              />
            </Headless.Field>
          </div>
        </Fieldset>

        <div className="mt-6 flex justify-end gap-4">
          <Button type="submit">
            Submit
          </Button>
        </div>
      </form>

      <Alert 
        open={showAlert} 
        onClose={() => setShowAlert(false)}
      >
        <AlertTitle>Success</AlertTitle>
        <AlertDescription>
          Bill item has been created successfully.
        </AlertDescription>
      </Alert>
    </div>
  );
}